/* eslint no-underscore-dangle: ["error", { "allow": ["_id"]}] */

import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  convertUnixtTimeToDate,
  convertUnixtTimeToDateTime,
} from '../../lib/tools';
import * as types from '../../lib/constants';
import config from '../../config';
import '../../styles/timeline.css';
import MessagePlaceholder from '../Parts/MessagePlaceholder';
import { style } from './styles/TimeLineGridListsStyles';
import {
  getLastLevelTagNames,
  getMaxHierarchy,
} from './functions/TimeLineGridListsUtils';
import { getMediaIconSrc } from './functions/getMediaIconSrc';
import { ReactComponent as PlayCircle } from '../../images/common/play_circle.svg';

const { deleteData } = config.customRole.functionAccess;

const createItem = (
  intl,
  displayDeleteData,
  fullscreen,
  list,
  checkbox,
  openDetail,
  onCheck,
  selectedTab,
) => {
  const { formatMessage } = intl;
  const isDeleteData = fullscreen && displayDeleteData;
  const mediaType = list.mediaType || 0;
  const thumbnailUrl = list.thumbnailUrl || null;
  const displayDateInfo =
    (list.enabled && convertUnixtTimeToDateTime(list.recordedDate)) ||
    `${formatMessage({
      id: 'timeLine.timeLineLists.title.deleteDate',
    })} ${convertUnixtTimeToDate(list.updatedDate)}`;

  const displayDate = displayDateInfo.split(' ')[1];

  const onClickMediaDetail = `onClickMediaDetail${list.id}`;
  const lineChecked = checkbox.ids.indexOf(Number(list.id)) > -1;
  const linerClass =
    (lineChecked && 'timelineSelected clickableItem') || 'clickableItem';

  const maxHierarchyArray = getMaxHierarchy(list.tags);
  const lastLevelTagsNames = getLastLevelTagNames(list.tags, maxHierarchyArray);

  return (
    <div
      id={onClickMediaDetail}
      style={style.list(lineChecked)}
      key={list.id}
      role="presentation"
      onClick={() => openDetail(list.id)}
    >
      <div className={linerClass} style={style.listBody}>
        <div style={style.mediaData}>
          <img
            src={getMediaIconSrc(mediaType, thumbnailUrl)}
            alt="IMG"
            style={style.mediaIcon}
          />
          {mediaType === types.VIDEO && thumbnailUrl && (
            <PlayCircle style={style.playCircle} />
          )}
        </div>
        <div style={style.info}>
          {isDeleteData && (
            <div style={style.checkbox}>
              <div
                style={style.checkArea}
                id={`timelineCheckArea${list.id}`}
                role="presentation"
                onClick={e => {
                  e.stopPropagation();
                  onCheck(`${list.id}`, !lineChecked);
                }}
              >
                <div
                  style={style.checkboxArea}
                  id={`timelineCheckboxArea${list.id}`}
                  role="presentation"
                  onClick={e => e.stopPropagation()}
                >
                  <input
                    id={`timelineCheckbox${list.id}`}
                    style={style.defaultCheckBox}
                    type="checkbox"
                    value={list.id}
                    onChange={e => onCheck(e.target.value, e.target.checked)}
                    checked={lineChecked ? 'checked' : ''}
                  />
                  <label
                    className="timelineCheckboxLabel"
                    style={style.checkboxLabel}
                    htmlFor={`timelineCheckbox${list.id}`}
                  />
                </div>
              </div>
            </div>
          )}
          {selectedTab === types.THUMBNAIL_TITLE_DISPLAY_MODE.dateDisplay && (
            <div style={style.date}>{displayDateInfo}</div>
          )}
          {selectedTab === types.THUMBNAIL_TITLE_DISPLAY_MODE.userDisplay && (
            <div style={style.date}>
              {`${displayDate} `}
              {list.operatorName}
            </div>
          )}
          {selectedTab === types.THUMBNAIL_TITLE_DISPLAY_MODE.tagDisplay && (
            <div style={style.date}>
              <p style={{ paddingRight: 3 }}>{`${displayDate}`}</p>
              {list.tags.map((tag, index) => {
                const tagCount = Object.keys(tag).filter(
                  key => tag[key] != null,
                ).length;

                return (
                  <p key={`tag-${tag.id}`}>
                    {`${tag?.tag1?.name}`}
                    {tagCount === 2 && ' > '}
                    {tagCount > 2 && ' > ... >'}
                    {lastLevelTagsNames[index] &&
                      ` ${lastLevelTagsNames[index]}`}
                    {index !== list.tags.length - 1 && ','}
                  </p>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const createItemBox = (
  intl,
  itemList,
  key,
  displayDeleteData,
  fullscreen,
  checkbox,
  openDetail,
  onCheck,
  selectedTab,
) => (
  <div key={key} style={style.body}>
    <div style={style.listHeader}>{key}</div>
    {itemList[key] &&
      itemList[key].map(list =>
        createItem(
          intl,
          displayDeleteData,
          fullscreen,
          list,
          checkbox,
          openDetail,
          onCheck,
          selectedTab,
        ),
      )}
  </div>
);

const TimeLineGridLists = ({
  openDetail,
  innerRole,
  fullscreen,
  timelines,
  checkbox,
  onCheck,
  selectedTab,
}) => {
  const intl = useIntl();
  const displayDeleteData = deleteData.indexOf(innerRole) !== -1;
  const itemList = {};
  timelines.forEach(element => {
    const {
      recordedDate,
      operatorName,
      freeTag,
      mediaType,
      updatedDate,
      id,
      tags,
      thumbnailUrl,
      enabled,
    } = element;
    const date = convertUnixtTimeToDate(recordedDate);
    itemList[date] = itemList[date] || [];
    itemList[date].push({
      operatorName,
      recordedDate,
      freeTag,
      id,
      updatedDate,
      mediaType,
      tags,
      thumbnailUrl,
      enabled,
    });
  });

  return (
    <div style={style.main} className="timeLineListsts">
      <div style={style.scrolledBody} id="canScrollTable">
        <div style={style.table}>
          {(itemList &&
            Object.keys(itemList).length > 0 &&
            Object.keys(itemList).map(key =>
              createItemBox(
                intl,
                itemList,
                key,
                displayDeleteData,
                fullscreen,
                checkbox,
                openDetail,
                onCheck,
                selectedTab,
              ),
            )) || <MessagePlaceholder />}
        </div>
      </div>
    </div>
  );
};

TimeLineGridLists.propTypes = {
  openDetail: PropTypes.func.isRequired,
  innerRole: PropTypes.number.isRequired,
  fullscreen: PropTypes.bool.isRequired,
  timelines: PropTypes.array.isRequired,
  checkbox: PropTypes.object.isRequired,
  onCheck: PropTypes.func.isRequired,
  selectedTab: PropTypes.number,
};

/**
 * 最近削除された項目では、サムネイルタイトルに日時を表示する
 */
TimeLineGridLists.defaultProps = {
  selectedTab: types.THUMBNAIL_TITLE_DISPLAY_MODE.dateDisplay,
};

export default TimeLineGridLists;
